import {
  Button,
  ButtonDropdown,
  Container,
  Grid,
  Header,
  Modal,
  Popover,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { ProcessNames } from '@parallel-fluidics/shared/constants';
import PLCToggleButton from 'features/plc/components/PLCToggleButton';
import {
  BONDING_TEMP_FIELDS_BY_SIDE,
  PRESS_HMI_BONDING_CONFIG_FIELDS,
} from 'features/plc/contracts/pressHMI.fields';
import { PLC_PATHS } from 'features/plc/contracts/pressHMI.schema';
import { usePLCValue, usePLCWriteState } from 'features/plc/providers/PLCProvider';
import ProcessLogPanel from 'features/processLog/components/ProcessLogPanel';
import React, { useMemo, useState } from 'react';

import ConfigSection from '../components/configSection';
import LoadConfigModal from '../components/loadConfigModal';
import ProcessStatus from '../components/processStatus';
import StartRunModal from '../components/startRunModal';
import TemperaturePanel from '../components/temperaturePanel';

const getTemperatureFieldBounds = ({
  field,
  lowerSetpointMax,
  lowerSetpointMin,
  upperSetpointMax,
  upperSetpointMin,
}) => {
  if (field.section !== 'Recipe Temperatures') {
    return {};
  }

  if (field.key.endsWith('Tolerance')) {
    return { min: 0 };
  }

  if (field.group === 'Chip Side (Upper)') {
    return {
      min: typeof upperSetpointMin === 'number' ? upperSetpointMin : field.min,
      max: typeof upperSetpointMax === 'number' ? upperSetpointMax : field.max,
    };
  }

  if (field.group === 'Cap Side (Lower)') {
    return {
      min: typeof lowerSetpointMin === 'number' ? lowerSetpointMin : field.min,
      max: typeof lowerSetpointMax === 'number' ? lowerSetpointMax : field.max,
    };
  }

  return {};
};

export default function AutomationControls() {
  const { runWrite } = usePLCWriteState();
  const safetyOk = usePLCValue(PLC_PATHS.status.safetyOk);
  const automationEnabled = usePLCValue(PLC_PATHS.cmd.automationEnable);
  const processLogId = usePLCValue(PLC_PATHS.meta.processLogId);
  const upperSetpointMin = usePLCValue(PLC_PATHS.cmd.upperHeatZoneConfig.setpointMin);
  const upperSetpointMax = usePLCValue(PLC_PATHS.cmd.upperHeatZoneConfig.setpointMax);
  const lowerSetpointMin = usePLCValue(PLC_PATHS.cmd.lowerHeatZoneConfig.setpointMin);
  const lowerSetpointMax = usePLCValue(PLC_PATHS.cmd.lowerHeatZoneConfig.setpointMax);
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [isLoadModalOpen, setIsLoadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const modalConfigFields = useMemo(
    () => PRESS_HMI_BONDING_CONFIG_FIELDS
      .filter((field) => field.section !== 'Automation')
      .map((field) => ({
        ...field,
        ...getTemperatureFieldBounds({
          field,
          lowerSetpointMax,
          lowerSetpointMin,
          upperSetpointMax,
          upperSetpointMin,
        }),
      })),
    [lowerSetpointMax, lowerSetpointMin, upperSetpointMax, upperSetpointMin],
  );

  let automationAction = (
    <Button onClick={() => { setIsStartModalOpen(true); }}>Enable automation</Button>
  );

  if (automationEnabled) {
    automationAction = (
      <Button
        variant="primary"
        onClick={() => {
          runWrite({ path: PLC_PATHS.cmd.automationEnable, value: false });
        }}
      >
        Disable automation
      </Button>
    );
  } else if (!safetyOk) {
    automationAction = (
      <Popover
        dismissButton={false}
        size="small"
        triggerType="custom"
        content={<StatusIndicator type="error">Safety is not OK</StatusIndicator>}
      >
        <Button>Enable automation</Button>
      </Popover>
    );
  }

  return (
    <Container
      header={(
        <Header
          variant="h2"
          actions={(
            <SpaceBetween direction="horizontal" size="xs">
              <ButtonDropdown
                items={[
                  {
                    text: 'Initialize configuration',
                    id: 'initialize',
                  },
                  {
                    text: 'Load previous configuration',
                    id: 'load',
                  },
                ]}
                onItemClick={({ detail }) => {
                  if (detail.id === 'load') {
                    setIsLoadModalOpen(true);
                  } else if (detail.id === 'initialize') {
                    setIsEditModalOpen(true);
                  }
                }}
              >
                Configuration
              </ButtonDropdown>
              {automationAction}
            </SpaceBetween>
          )}
        >
          Automation controls
        </Header>
      )}
    >
      <StartRunModal
        isOpen={isStartModalOpen}
        close={() => { setIsStartModalOpen(false); }}
        process={ProcessNames.BONDING}
      />
      <LoadConfigModal
        isOpen={isLoadModalOpen}
        close={() => { setIsLoadModalOpen(false); }}
        process={ProcessNames.BONDING}
      />
      <Modal
        size="max"
        visible={isEditModalOpen}
        onDismiss={() => { setIsEditModalOpen(false); }}
        header="Bonding Automation Configuration"
      >
        <ConfigSection
          configFields={modalConfigFields}
          editable
        />
      </Modal>
      <Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
        <TemperaturePanel
          title="Chip Side (Upper)"
          side="upper"
          fields={BONDING_TEMP_FIELDS_BY_SIDE.upper}
        >
          <Button variant="inline-link" onClick={() => setIsEditModalOpen(true)}>View/Edit Configuration</Button>
        </TemperaturePanel>
        <TemperaturePanel
          title="Cap Side (Lower)"
          side="lower"
          fields={BONDING_TEMP_FIELDS_BY_SIDE.lower}
        />
        <SpaceBetween size="s">
          <ProcessStatus showVacuumPressure={false} />
          <PLCToggleButton
            path={PLC_PATHS.cmd.standbyHeatToLoadingEnable}
            trueLabel="Disable standby heating"
            falseLabel="Enable standby heating"
          />
          <PLCToggleButton
            path={PLC_PATHS.cmd.bondingRecipe.autoIonizer}
            trueLabel="Disable ionizer automation"
            falseLabel="Enable ionizer automation"
          />
          <PLCToggleButton
            path={PLC_PATHS.cmd.bondingRecipe.bondingVacuumEnable}
            trueLabel="Disable vacuum"
            falseLabel="Enable vacuum"
          />
        </SpaceBetween>
        <ProcessLogPanel processLogId={processLogId} />
      </Grid>
    </Container>
  );
}
